import * as React from "react";
import {Trans, useTranslation} from "react-i18next";
import InfoIcon from "../../../lib/InfoIcon/InfoIcon";
import {usePaymentContext} from "./Payment";
import {useRef} from "react";
import {pad} from "../Shipping/ShippingDatePicker";
import styles from "./PaymentBirthdayPicker.module.scss";

interface IPaymentBirthdayPickerProps {
  isScoringRequired: boolean;
  isSelectedPayment: boolean;
}

const liveYears = 120;

/**
 * Birthday picker for payment rows that require scoring - only visible when selected
 * @param props
 * @constructor
 */
const PaymentBirthdayPicker: React.FC<IPaymentBirthdayPickerProps> = (props: IPaymentBirthdayPickerProps) => {
  const {t} = useTranslation("checkout");
  const {dateOfBirthRef} = usePaymentContext();
  const dayRef = useRef<HTMLSelectElement>(null);
  const monthRef = useRef<HTMLSelectElement>(null);
  const yearRef = useRef<HTMLSelectElement>(null);

  const {isScoringRequired, isSelectedPayment} = props;

  if (!(isScoringRequired && isSelectedPayment)) {
    return null;
  }

  const earliestYear = new Date().getUTCFullYear() - liveYears;

  const checkDate = () => {
    if (
      dayRef.current === null ||
      dayRef.current.value === "Day" ||
      monthRef.current === null ||
      monthRef.current.value === "Month" ||
      yearRef.current === null ||
      yearRef.current.value === "Year"
    ) {
      return;
    }

    const [day, month, year] = [
      Number(dayRef.current.value),
      Number(monthRef.current.value),
      Number(yearRef.current.value),
    ];
    const generatedDay = new Date(year, month - 1, day).getDate();
    if (day !== generatedDay) {
      dayRef.current.value = (day - 1).toString();
      checkDate();

      return;
    }
    dateOfBirthRef.current = `${dayRef.current.value}.${monthRef.current.value}.${yearRef.current.value}`;
  };

  return (
    <div>
      <span>{t("Geburtstag")}:</span>
      <div className={styles.wrapper}>
        <select ref={dayRef} name="Day" defaultValue={"Day"} onChange={checkDate}>
          <option key="DayHint" value="Day" disabled={true}>
            {t("Tag")}
          </option>
          {[...Array<number>(31)].map((value, index) => {
            return (
              <option key={index} value={pad(index + 1)}>
                {index + 1}
              </option>
            );
          })}
        </select>
        <select ref={monthRef} name="Month" defaultValue={"Month"} onChange={checkDate}>
          <option key="MonthHint" value="Month" disabled={true}>
            {t("Monat")}
          </option>
          {[
            t("Januar"),
            t("Februar"),
            t("März"),
            t("April"),
            t("Mai"),
            t("Juni"),
            t("Juli"),
            t("August"),
            t("September"),
            t("Oktober"),
            t("November"),
            t("Dezember"),
          ].map((value, index) => {
            return (
              <option key={`${value}+${index}`} value={pad(index + 1)}>
                {value}
              </option>
            );
          })}
        </select>
        <select ref={yearRef} name="Year" defaultValue={"Year"} onChange={checkDate}>
          <option key="YearHint" value="Year" disabled={true}>
            {t("Jahr")}
          </option>
          {[...Array<number>(liveYears)].map((value, index) => {
            const reversedValue = earliestYear + liveYears - index;

            return (
              <option key={index} value={reversedValue}>
                {reversedValue}
              </option>
            );
          })}
        </select>
        <InfoIcon>
          <Trans i18nKey="BirthdayNeeded" ns="checkout">
            <h3>Information</h3>
            <p>Wir benötigen das Geburtsdatum um Ihre Geschäftsfähigkeit zu überprüfen.</p>
            <p>
              Je nach Auftrag behalten wir es uns vor, eine Bonitätsüberprüfung durch die Creditreform durchzuführen.
            </p>
          </Trans>
        </InfoIcon>
      </div>
    </div>
  );
};

export default PaymentBirthdayPicker;
